import type { PropsWithChildren, RefObject } from "react";

import * as ComboboxPrimitives from "@ariakit/react/combobox";
import * as RadixPopover from "@radix-ui/react-popover";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

import { inputStyle } from "./input.styles";

const ComboboxRoot = RadixPopover.Root;
const ComboboxProvider = ComboboxPrimitives.ComboboxProvider;

const ComboboxInput = ({ ref, className, ...rest }: ComboboxPrimitives.ComboboxProps & { ref: React.RefObject<HTMLInputElement | null> }) => {
  return (
    <RadixPopover.Anchor asChild>
      <ComboboxPrimitives.Combobox
        {...rest}
        ref={ref}
        className={inputStyle(false, false, className)}
      />
    </RadixPopover.Anchor>
  );
};
ComboboxInput.displayName = "ComboboxInput";

const ComboboxItem = ({ ref, className, children, ...rest }: ComboboxPrimitives.ComboboxItemProps & { ref?: React.RefObject<HTMLDivElement> }) => {
  return (
    <ComboboxPrimitives.ComboboxItem
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-md py-3 pl-10 pr-3 text-sm font-medium leading-none tracking-tight text-white outline-none hover:bg-neutral-500 focus:bg-neutral-500 focus:text-white aria-selected:bg-neutral-500 aria-selected:text-white data-[disabled]:pointer-events-none data-[focus-visible=true]:bg-neutral-500 data-[disabled]:opacity-50",
        className,
      )}
      focusOnHover
      setValueOnClick
      {...rest}
    >
      <span className="absolute left-4 flex size-3.5 items-center justify-center">
        <ComboboxPrimitives.ComboboxItemCheck>
          <Check className="size-4" />
        </ComboboxPrimitives.ComboboxItemCheck>
      </span>
      {children}
    </ComboboxPrimitives.ComboboxItem>
  );
};
ComboboxItem.displayName = "ComboboxItem";

function ComboboxContent({
  children,
  comboboxRef,
  listboxRef,
  className,
}: PropsWithChildren<{
  comboboxRef: RefObject<HTMLInputElement | null>;
  listboxRef: RefObject<HTMLDivElement | null>;
  className?: string;
}>) {
  return (
    <RadixPopover.Portal>
      <RadixPopover.Content
        align="start"
        asChild
        sideOffset={8}
        onOpenAutoFocus={event => event.preventDefault()}
        onInteractOutside={(event) => {
          const target = event.target as Element | null;
          const isCombobox = target === comboboxRef.current;
          const inListbox = target && listboxRef.current?.contains(target);
          if (isCombobox || inListbox) {
            event.preventDefault();
          }
        }}
        className={cn(
          "text-md z-[60] max-h-[200px] overflow-y-scroll rounded-lg bg-neutral-800 shadow-md",
          "border border-neutral-600 p-2",
          className,
        )}
      >
        {children}
      </RadixPopover.Content>
    </RadixPopover.Portal>
  );
}

const ComboboxList = ({ ref, className, ...rest }: ComboboxPrimitives.ComboboxListProps & { ref: React.RefObject<HTMLDivElement | null> }) => {
  return (
    <ComboboxPrimitives.ComboboxList
      ref={ref}
      role="listbox"
      className={cn("flex w-[--radix-popover-trigger-width] flex-col bg-neutral-800", className)}
      {...rest}
    />
  );
};
ComboboxList.displayName = "ComboboxList";

export { ComboboxContent, ComboboxInput, ComboboxItem, ComboboxList, ComboboxProvider, ComboboxRoot };
